/**
 * "Mehr" — the rest of the app, on a phone.
 *
 * The four tab-bar slots hold what a pilot touches in flight preparation; this
 * sheet holds everything else, in the order the rail has it. Extra entries (the
 * workspace's remaining panes) come first, because that is what the tab that
 * opened this sheet was about.
 */
import { useEffect, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../i18n';
import { useSession } from '../session';
import { IconAircraft, IconFlights, IconSettings, IconSources } from './icons';

export interface MoreEntry {
  key: string;
  label: string;
  Icon: ComponentType<{ size?: number }>;
  /** a switch rather than a command — rendered pressed, and it does not close the sheet */
  checked?: boolean;
  /** clears the plan: drawn in the warning ink, wherever it sits in the list */
  danger?: boolean;
  onSelect(): void;
}

export function MoreSheet({
  entries = [],
  onClose,
}: {
  entries?: readonly MoreEntry[];
  onClose(): void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const session = useSession();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const go = (to: string) => () => {
    onClose();
    navigate(to);
  };

  const nav: MoreEntry[] = [
    { key: 'flights', label: t('nav.flights'), Icon: IconFlights, onSelect: go('/flights') },
    { key: 'aircraft', label: t('nav.aircraft'), Icon: IconAircraft, onSelect: go('/aircraft') },
    { key: 'settings', label: t('nav.settings'), Icon: IconSettings, onSelect: go('/settings') },
    { key: 'sources', label: t('nav.sources'), Icon: IconSources, onSelect: go('/sources') },
  ];
  if (!session.user) {
    nav.push({ key: 'signin', label: t('nav.signin'), Icon: IconSettings, onSelect: go('/login') });
  }

  return (
    <div
      className="ae-moresheet"
      role="dialog"
      aria-modal="true"
      aria-label={t('nav.more')}
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      <div>
        {entries.map(e => (
          <button
            key={e.key}
            type="button"
            className={e.danger ? 'danger' : undefined}
            {...(e.checked === undefined ? {} : { 'aria-pressed': e.checked })}
            /* A command closes the sheet, a switch does not. Without this, the
               pane a pilot picked opened UNDER a full-screen modal that never
               went away — the pane looked unreachable, which is what the
               "Gelände/Formular/Export not reachable on iPhone" report was. */
            onClick={() => {
              e.onSelect();
              if (e.checked === undefined) onClose();
            }}
          >
            <e.Icon size={20} />
            <span>{e.label}</span>
            {e.checked === undefined ? null : (
              <span className="ae-sheet-check" aria-hidden="true">
                {e.checked ? '✓' : ''}
              </span>
            )}
          </button>
        ))}
        {entries.length ? <hr className="ae-sheet-rule" /> : null}
        {nav.map(e => (
          <button key={e.key} type="button" onClick={e.onSelect}>
            <e.Icon size={20} />
            <span>{e.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
